import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { BaseService } from 'src/app/services/base.service';
import { HttpResponse } from '@angular/common/http';

@Injectable({ providedIn: 'root' })
export class SoilCropNutritionService extends BaseService {
  private recManUrl = this.API_ADMIN_BASE_URL + 'v1/recommendation-manager';
  private fertUrl = this.API_ADMIN_BASE_URL + 'v1/recommendation-manager/fertilization-program';
  private labMetaUrl = 'https://admin-staging.yaraallfarm.com/v1/lab-management/meta';

  private products$: Observable<any[]>;
  private templates$: Observable<any[]>;

  constructor(private http: HttpClient) {
    super();
    this.products$ = this.http.get<any[]>(`${this.fertUrl}/products`).pipe(shareReplay(1));
    this.templates$ = this.http.get<any[]>(`${this.fertUrl}/templates`).pipe(shareReplay(1));
  }

  // Get customer list from lab-management
  getCustomers(): Observable<any[]> {
    return this.http.get<any[]>(`${this.labMetaUrl}/customers`);
  }

  // Meta - get farms and fields for a tenant
  getTenantFarmsFields(tenantId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.recManUrl}/meta/tenant-farms-fields?tenantId=${tenantId}`);
  }

  // Create a Soil Crop Nutrition recommendation linking fields
  createSoilCropNutrition(clientId: number, fieldIds: number[]): Observable<any> {
    return this.http.post(`${this.recManUrl}/soil-crop-nutrition`, { clientId, fieldIds });
  }

  // Create with season selections
  /**
   * @param fieldDefinitions crop definition ids per field. A field with no entry
   *   produces a single link with no definition, which is the pre-multi-cultivar
   *   behaviour.
   */
  createSoilCropNutritionWithSeasons(
    clientId: number,
    fieldIds: number[],
    fieldSeasons: Record<number, number>,
    fieldDefinitions?: Record<number, number[]>
  ): Observable<any> {
    return this.http.post(`${this.recManUrl}/soil-crop-nutrition`, { clientId, fieldIds, fieldSeasons, fieldDefinitions });
  }

  // Get seasons for selected fields
  getFieldSeasons(fieldIds: number[]): Observable<any[]> {
    return this.http.post<any[]>(`${this.recManUrl}/soil-crop-nutrition/fields/seasons`, fieldIds);
  }

  // Get the Soil Crop Nutrition recommendation (with linked fields)
  getSoilCropNutritionRec(recId: number): Observable<any> {
    return this.http.get(`${this.recManUrl}/soil-crop-nutrition/${recId}`);
  }

  // Get LAST soil maintenance state for a field (new API endpoint needed)
  getLastSoilMaintenance(fieldId: number): Observable<any> {
    return this.http.get(`${this.recManUrl}/api/soil-maintenance/field/${fieldId}/latest`);
  }

  // Get LAST leaf interpretation for a field (new API endpoint needed)
  getLastLeafRecommendation(fieldId: number): Observable<any> {
    return this.http.get(`${this.recManUrl}/soft-tissue/field/${fieldId}/latest`);
  }

  // Get unified field data (top info bar, soil, leaf, previous SCN)
  /**
   * @param seasonFieldDetailId the crop definition this tab covers. Omitted for
   *   recommendations created before multi-cultivar support, where the API
   *   resolves the definition the way it always did.
   */
  getFieldData(fieldId: number, recId?: number, seasonFieldDetailId?: number): Observable<any> {
    const parts: string[] = [];
    if (recId != null) parts.push(`recId=${recId}`);
    if (seasonFieldDetailId != null) parts.push(`seasonFieldDetailId=${seasonFieldDetailId}`);
    const params = parts.length ? `?${parts.join('&')}` : '';
    return this.http.get(`${this.recManUrl}/soil-crop-nutrition/field/${fieldId}/data${params}`);
  }

  // Get soil section for a specific recommendation
  getSoilByRec(fieldId: number, recId: number): Observable<any> {
    return this.http.get(`${this.recManUrl}/soil-crop-nutrition/field/${fieldId}/soil?recId=${recId}`);
  }

  // Get leaf section for a specific recommendation
  getLeafByRec(fieldId: number, recId: number): Observable<any> {
    return this.http.get(`${this.recManUrl}/soil-crop-nutrition/field/${fieldId}/leaf?recId=${recId}`);
  }

  // Get ALL leaf samples for a field (for "Show More" view)
  getAllLeafSamples(fieldId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.recManUrl}/soil-crop-nutrition/field/${fieldId}/leaf-all`);
  }

  // Get trend data for charts
  /**
   * @param seasonFieldDetailId the crop definition this tab covers. Only the leaf
   *   norm bands and the SCN series use it; the soil and leaf series stay
   *   field-scoped, because a trend over the ground is meant to run across
   *   seasons and crops.
   */
  getFieldTrends(fieldId: number, type: string, seasonFieldDetailId?: number): Observable<any> {
    const definition = seasonFieldDetailId != null ? `&seasonFieldDetailId=${seasonFieldDetailId}` : '';
    return this.http.get(`${this.recManUrl}/soil-crop-nutrition/field/${fieldId}/trends?type=${type}${definition}`);
  }

  // Get activity history
  getHistory(recId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.recManUrl}/soil-crop-nutrition/${recId}/history`);
  }

  // Save field selections (season, soil rec, leaf rec)
  saveFieldSelections(recId: number, fieldId: number, body: any): Observable<any> {
    return this.http.put(`${this.recManUrl}/soil-crop-nutrition/${recId}/field/${fieldId}/selections`, body);
  }

  // Crop Nutrition Plan PDFs. Year defaults to the current year server-side.
  generatePerHaReport(recId: number, year?: number): Observable<HttpResponse<Blob>> {
    const params = year != null ? `?year=${year}` : '';
    return this.http.post(`${this.recManUrl}/soil-crop-nutrition/${recId}/report/per-ha${params}`,
      null, { responseType: 'blob', observe: 'response' });
  }

  generatePerBlockReport(recId: number, year?: number): Observable<HttpResponse<Blob>> {
    const params = year != null ? `?year=${year}` : '';
    return this.http.post(`${this.recManUrl}/soil-crop-nutrition/${recId}/report/per-block${params}`,
      null, { responseType: 'blob', observe: 'response' });
  }

  /**
   * The per application plan: one sheet per block, with the programme running
   * down the page rather than blocks down and stages across.
   *
   * It answers a different question from the other two. Those compare blocks
   * against each other; this one is what to put on ONE block and when, and it
   * carries blank Date and Administered columns because it is meant to be
   * printed and written on while the work is done.
   *
   * The endpoint is still called block-programme. Renaming a live URL to match a
   * button label would break every caller for nothing.
   */
  generatePerApplicationReport(recId: number, year?: number): Observable<HttpResponse<Blob>> {
    const params = year != null ? `?year=${year}` : '';
    return this.http.post(`${this.recManUrl}/soil-crop-nutrition/${recId}/report/block-programme${params}`,
      null, { responseType: 'blob', observe: 'response' });
  }

  /**
   * The historical farm data report, which the client calls Plaasinligting: three years of
   * CNP per block on one page, used for discussions on the farm.
   *
   * Takes FARMS, not a recommendation, which is why it is the only one of these that does
   * not pass recId. It spans three years and therefore many recommendations, so there is no
   * single one to start from. A recommendation can also cover blocks on more than one farm,
   * which is why this takes a list and the workspace asks the user to pick: one PDF with a
   * section per farm, rather than a download each, because a browser blocks every download
   * after the first.
   *
   * Year is optional and the server defaults to the current one, always covering that year
   * and the two before. There is no picker: Access has none and the client did not want one.
   * The parameter is here so offering a longer window later is a front end change.
   */
  generateHistoricalFarmReport(farmIds: number[], year?: number): Observable<Blob> {
    const params = new URLSearchParams();
    farmIds.forEach(id => params.append('farmIds', String(id)));
    if (year != null) params.append('year', String(year));
    return this.http.post(`${this.recManUrl}/soil-crop-nutrition/report/historical?${params.toString()}`,
      null, { responseType: 'blob' });
  }

  // Fertilization Program CRUD
  /**
   * @param seasonFieldDetailId the crop definition whose schedule is wanted.
   *   Omitted falls back to the field's null-definition program, which is what
   *   every program created before per-cultivar support has.
   */
  getProgram(fieldId: number, year: number, seasonFieldDetailId?: number): Observable<any> {
    const definition = seasonFieldDetailId != null ? `&seasonFieldDetailId=${seasonFieldDetailId}` : '';
    return this.http.get(`${this.fertUrl}?fieldId=${fieldId}&year=${year}${definition}`);
  }

  saveProgram(program: any): Observable<any> {
    return this.http.post(this.fertUrl, program);
  }

  updateProgramStatus(programId: number, status: string): Observable<any> {
    return this.http.put(`${this.fertUrl}/${programId}/status`, { status });
  }

  /**
   * Moves the whole recommendation, not one tab's program.
   *
   * One request in place of the per-tab loop this replaces. The server validates
   * the transition, refuses to start when any field has no fertilization program,
   * and cascades to every program in one transaction, so there is no half-moved
   * state and nothing to reconcile when something fails.
   */
  updateScnStatus(recId: number, status: string): Observable<any> {
    return this.http.put(`${this.recManUrl}/soil-crop-nutrition/${recId}/status`, { status });
  }

  getProducts(): Observable<any[]> {
    return this.products$;
  }

  getTemplates(): Observable<any[]> {
    return this.templates$;
  }

  // User Schedules (Save as Schedule)
  getMySchedules(): Observable<any[]> {
    return this.http.get<any[]>(`${this.fertUrl}/my-schedules`);
  }

  saveAsSchedule(name: string, programId: number): Observable<any> {
    return this.http.post(`${this.fertUrl}/my-schedules`, { name, programId });
  }

  deleteMySchedule(id: number): Observable<any> {
    return this.http.delete(`${this.fertUrl}/my-schedules/${id}`);
  }

  // Report Notes
  // Keyed on the recommendation. Seeded on create from the user's defaults or
  // their previous SCN, then owned by this recommendation alone. Method names and
  // signatures match ReportNotesApi so this service can be handed straight to
  // SoilReportNotesDialogComponent.
  getReportNotes(recId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.recManUrl}/soil-crop-nutrition/${recId}/report-notes`);
  }

  addReportNote(recId: number, note: string): Observable<any> {
    return this.http.post(`${this.recManUrl}/soil-crop-nutrition/${recId}/report-notes`, { note });
  }

  updateReportNote(recId: number, noteId: number, note: string): Observable<any> {
    return this.http.put(`${this.recManUrl}/soil-crop-nutrition/${recId}/report-notes/${noteId}`, { note });
  }

  deleteReportNote(recId: number, noteId: number): Observable<any> {
    return this.http.delete(`${this.recManUrl}/soil-crop-nutrition/${recId}/report-notes/${noteId}`);
  }

  // Block Notes
  // Keyed on the link row, which IS the tab: a field plus its crop definition.
  // Two cultivars on one field are two tabs and keep separate notes.
  getBlockNotes(linkId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.recManUrl}/soil-crop-nutrition/blocks/${linkId}/notes`);
  }

  addBlockNote(linkId: number, note: string): Observable<any> {
    return this.http.post(`${this.recManUrl}/soil-crop-nutrition/blocks/${linkId}/notes`, { note });
  }

  updateBlockNote(linkId: number, noteId: number, note: string): Observable<any> {
    return this.http.put(`${this.recManUrl}/soil-crop-nutrition/blocks/${linkId}/notes/${noteId}`, { note });
  }

  deleteBlockNote(linkId: number, noteId: number): Observable<any> {
    return this.http.delete(`${this.recManUrl}/soil-crop-nutrition/blocks/${linkId}/notes/${noteId}`);
  }
}
